import * as React from 'react'
import {ReactElement, ReactNode} from 'react'
import {Nullable} from '../../../../../app/TypeHelpers'
import Timeline, {
    DateHeader,
    IntervalRenderer,
    ReactCalendarGroupRendererProps,
    TimelineGroupBase,
    TimelineHeaders,
    TimelineItemBase,
} from 'react-calendar-timeline'
import moment, {Moment} from 'moment-timezone'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../../../../styledComponents/Constants'
import {MaxWidth} from '../../../../../components/reservations/components/ReservationTimelineView'
import {
    BusinessInfo,
    NatGetAssocWorkReportPlanResultsDetail,
    PlanResults,
    ResultFile
} from '../../repository/NatGetAssocWorkReportPlanResults'
import {PlainLinkStyledButton} from '../../../../../styledComponents/Button'
import {BUSINESS_CATEGORY, PLAN_EXISTENCE} from '../../../../../CodeMaster'
import {Box, IconButton} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {ColumnFlexContainer, StretchColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {NavigateBefore, NavigateNext} from '@mui/icons-material'
import {isWidthDown, WithWidth, WithWidthProps} from '../../../../../components/shared/forms/hoc/withMediaQuery'
import {ItemRendererProps} from 'react-calendar-timeline/dist/lib/items/Item'
import {faDownload} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

interface Props {
    accountingTerm: AccountingTerm
    assocWorkReportPlanResultsDetail: NatGetAssocWorkReportPlanResultsDetail
    mergedFileSizeByYearMonthMap: Map<string, number>
    maxWidth: MaxWidth
    onBusinessNameClick: (business: BusinessInfo) => void
    onDownloadIconClick: (yearMonth: string) => void
    onResultClick: (businessName: string, resultFile: ResultFile[]) => void
}

interface State {
    navigateIcon: NavigateIcon
    schedule: Nullable<ScheduleItem>
    currentTime: Moment
    minTime: number
    maxTime: number
    monthsDiff: number
    visibleTimeStart: number
    visibleTimeEnd: number
    width?: number
}

interface AccountingTerm {
    accountingTermKey: string
    startYearMonth: string
    endYearMonth: string
    stopYearMonth?: string
}

interface NavigateIcon {
    nextIconDisplay: boolean
    backIconDisplay: boolean
}

interface ScheduleItem {
    group: CustomGroup[]
    item: CustomItem[]
}

interface CustomGroup extends TimelineGroupBase {
    categoryFlag?: boolean
    height: number,
    className?: string
    businessCategory?: string
    businessInfo?: BusinessInfo
}

interface CustomItem extends TimelineItemBase<number> {
    categoryFlag?: boolean
    height: number,
    current?: boolean
    stop?: boolean
    businessName?: string
    resultFiles?: ResultFile[]
}

class NatAssocWorkReportTimelineBase extends React.Component<Props & WithWidthProps, State> {
    constructor(props: Readonly<Props & WithWidthProps>) {
        super(props)

        const startTime = moment(this.props.accountingTerm.startYearMonth, 'YYYYMM').startOf('month').valueOf()
        const endTime = moment(this.props.accountingTerm.endYearMonth, 'YYYYMM').endOf('month').valueOf()
        this.state = {
            navigateIcon: {
                nextIconDisplay: false,
                backIconDisplay: false,
            },
            schedule: null,
            currentTime: moment().startOf('month'),
            minTime: startTime,
            maxTime: endTime,
            monthsDiff: moment(endTime.valueOf()).diff(moment(startTime).valueOf(), 'month'),
            visibleTimeStart: startTime,
            visibleTimeEnd: endTime,
            width: this.props.width,
        }

        this.handleTimeChange = this.handleTimeChange.bind(this)
    }

    componentDidMount(): void {
        this.setPlanResult()
        this.setVisibleTerm()
    }

    componentDidUpdate(
        prevProps: Readonly<Props & WithWidthProps>, prevState: Readonly<State>, snapshot?: any,
    ): void {
        if (prevProps.width !== this.props.width) {
            this.setState({width: this.props.width})
            this.setVisibleTerm()
        }
        if (prevProps.accountingTerm.accountingTermKey !== this.props.accountingTerm.accountingTermKey) {
            this.setState({
                minTime: moment(this.props.accountingTerm.startYearMonth, 'YYYYMM').startOf('month').valueOf(),
                maxTime: moment(this.props.accountingTerm.endYearMonth, 'YYYYMMDD').endOf('month').valueOf()
            }, () => {
                this.setPlanResult()
                this.setVisibleTerm()
            })
        }
    }

    setPlanResult(): void {
        const {planResults} = this.props.assocWorkReportPlanResultsDetail
        const heightMap = this.heightCalculator(planResults)

        const planResultsSort = [...planResults].sort((planResult) => {
            if (planResult.businessCategory === BUSINESS_CATEGORY.ACCOUNTING_MONTHLY_REPORT.value) {
                return -1
            }
            return 1
        })

        const group = planResultsSort.flatMap((result) => {
            return this.toGroup(result.businessCategory, result, heightMap)
        })
        const item = planResultsSort.flatMap((result) => {
            return this.toItem(result.businessCategory, result, heightMap)
        })

        const scheduleItem: ScheduleItem = {
            group: group,
            item: item,
        }
        this.setState({schedule: scheduleItem})
    }

    heightCalculator(planResults: PlanResults[]): Map<string, number> {
        const heightMap = new Map<string, number>()

        for (const planResult of planResults) {
            for (const businessInfo of planResult.businessInfo) {
                // 管理事務報告画面のタイムライン対応(#6868)として、20文字以上で高さ128になる制御をなくし高さ64で固定に変更
                const height = 64

                heightMap.set(businessInfo.workingCd, height)
            }
        }

        return heightMap
    }

    toGroup(
        businessCategory: string,
        planResults: PlanResults,
        heightPerWorkCode: Map<string, number>
    ): CustomGroup[] {
        const group: CustomGroup[] = planResults.businessInfo.map(business => {
            const height = heightPerWorkCode.get(business.workingCd)
            return {
                id: `assoc-work-report-${business.workingCd}`,
                title: business.businessName,
                businessCategory: planResults.businessCategory,
                businessInfo: business,
                height: height !== undefined ? height : 64,
            }
        })

        const workingCodes = planResults.businessInfo.map(business => business.workingCd)

        if (businessCategory !== BUSINESS_CATEGORY.ACCOUNTING_MONTHLY_REPORT.value) {
            group[0].className = 'custom-group'
            group.unshift({
                id: `assoc-work-report-${workingCodes}-category`,
                title: planResults.businessCategoryName,
                height: 64,
                className: 'custom-group',
                categoryFlag: true
            })
        }
        return group
    }

    toItem(
        businessCategory: string,
        planResults: PlanResults,
        heightPerWorkCode: Map<string, number>
    ): CustomItem[] {
        const businessInfo = planResults.businessInfo
        const {stopYearMonth, endYearMonth} = this.props.accountingTerm
        const currentStartMoment = moment().startOf('month')
        const currentEndTMoment = moment().endOf('month')
        const stopMoment = moment(stopYearMonth, 'YYYYMM').startOf('month')
        const endMoment = moment(endYearMonth, 'YYYYMM').endOf('month')

        const customItemMap = new Map<string, CustomItem>()

        businessInfo.forEach((business) => {
            if (!stopYearMonth || !currentStartMoment.isSameOrAfter(stopMoment)) {
                const height = heightPerWorkCode.get(business.workingCd)

                customItemMap.set(`${business.workingCd}-${currentStartMoment.format('YYYYMM')}`,
                    {
                        id: `assoc-work-report-${business.workingCd}`,
                        group: `assoc-work-report-${business.workingCd}`,
                        title: (<></>),
                        start_time: currentStartMoment.valueOf(),
                        end_time: currentEndTMoment.valueOf(),
                        current: true,
                        height: height !== undefined ? height : 64,
                    }
                )
            }
            business.report.forEach((report, key) => {
                const startTimeMoment = moment(report.targetYearMonth, 'YYYYMM').startOf('month')
                const endTimeMoment = moment(report.targetYearMonth, 'YYYYMM').endOf('month')

                if (!stopYearMonth ||
                    (!stopMoment.isSame(startTimeMoment) &&
                        startTimeMoment.isBefore(stopMoment))) {
                    const height = heightPerWorkCode.get(business.workingCd)

                    customItemMap.set(`${business.workingCd}-${startTimeMoment.format('YYYYMM')}`,
                        {
                            id: `${business.workingCd}-${key}-${report.targetYearMonth}`,
                            group: `assoc-work-report-${business.workingCd}`,
                            title: (<>{report.planResult ? report.planResult :
                                report.planExistence === PLAN_EXISTENCE.HAVE.value ? '〇' : ''}</>),
                            start_time: startTimeMoment.valueOf(),
                            end_time: endTimeMoment.valueOf(),
                            current: startTimeMoment.isSame(currentStartMoment),
                            height: height !== undefined ? height : 64,
                            resultFiles: report.resultFiles,
                            businessName: business.businessName
                        }
                    )
                }
            })

            if (stopYearMonth) {
                const height = heightPerWorkCode.get(business.workingCd)

                customItemMap.set(`${business.workingCd}-${endMoment.format('YYYYMM')}`,
                    {
                        id: `${business.workingCd}-${Math.random()}`,
                        group: `assoc-work-report-${business.workingCd}`,
                        title: (<></>),
                        start_time: stopMoment.valueOf(),
                        end_time: endMoment.valueOf(),
                        stop: true,
                        height: height !== undefined ? height : 64,
                    }
                )
            }
        })

        const item = Array.from(customItemMap.values())

        if (businessCategory !== BUSINESS_CATEGORY.ACCOUNTING_MONTHLY_REPORT.value) {
            const workingCodes = planResults.businessInfo.map(business => business.workingCd)

            item.unshift({
                id: `assoc-work-report-${workingCodes}-category-item`,
                group: `assoc-work-report-${workingCodes}-category`,
                title: (<></>),
                start_time: this.state.minTime,
                end_time: this.state.maxTime,
                height: 64,
                categoryFlag: true
            })
        }
        return item
    }

    setVisibleTerm(): void {
        const month = this.calculateMonthDuration()
        this.setState(prevState => {
                const visibleTimeStart = (prevState.visibleTimeStart + month) > this.state.maxTime ?
                    this.state.maxTime - month : prevState.visibleTimeStart
                const visibleTimeEnd = (prevState.visibleTimeStart + month) > this.state.maxTime ?
                    this.state.maxTime : prevState.visibleTimeStart + month
                return {
                    visibleTimeStart: visibleTimeStart,
                    visibleTimeEnd: visibleTimeEnd
                }
            }, () =>
                this.updateIconDisplay(this.state.visibleTimeStart, this.state.visibleTimeEnd)
        )
    }

    calculateMonthDuration(): number {
        const {width} = this.props
        if (!width) {
            return 0
        }
        const monthsDiff = moment(this.state.maxTime.valueOf()).diff(moment(this.state.minTime).valueOf(), 'month')

        const spMode = isWidthDown(width, true)
        if (spMode && monthsDiff >= 1) {
            const start = moment().startOf('months')
            // 当月とその次が少し切れるくらいの横幅を実現のため、可視終了時間を調整
            const end = moment().startOf('months').add({days: 25, month: 1})
            return end.valueOf() - start.valueOf()
        }

        if (monthsDiff >= 6) {
            const start = moment().startOf('months')
            // 会計期に含まれる月が7以上の場合、7つ目の月が少しだけ見える横幅のため、可視終了時間を調整
            const end = moment().startOf('months').add({days: 10, month: 6})
            return end.valueOf() - start.valueOf()
        }

        return this.state.maxTime - this.state.minTime
    }

    handleTimeChange(
        visibleTimeStart: number,
        visibleTimeEnd: number,
        updateScrollCanvas: (start: number, end: number) => void,
    ): void {
        const {minTime, maxTime} = this.state
        let updateVisibleTimeStart: number
        let updateVisibleTimeEnd: number

        if (visibleTimeStart < minTime && visibleTimeEnd > maxTime) {
            updateVisibleTimeStart = minTime
            updateVisibleTimeEnd = maxTime
        } else if (visibleTimeStart < minTime) {
            updateVisibleTimeStart = minTime
            updateVisibleTimeEnd = minTime + (visibleTimeEnd - visibleTimeStart)
        } else if (visibleTimeEnd > maxTime) {
            updateVisibleTimeStart = maxTime - (visibleTimeEnd - visibleTimeStart)
            updateVisibleTimeEnd = maxTime
        } else {
            updateVisibleTimeStart = visibleTimeStart
            updateVisibleTimeEnd = visibleTimeEnd
        }

        this.updateIconDisplay(updateVisibleTimeStart, updateVisibleTimeEnd)
        updateScrollCanvas(updateVisibleTimeStart, updateVisibleTimeEnd)
        this.setState({
            visibleTimeStart: updateVisibleTimeStart,
            visibleTimeEnd: updateVisibleTimeEnd,
        })
    }

    updateIconDisplay(
        visibleTimeStart: number,
        visibleTimeEnd: number
    ): void {
        const {minTime, maxTime} = this.state

        if (minTime === visibleTimeStart && maxTime === visibleTimeEnd) {
            this.setState({navigateIcon: {nextIconDisplay: false, backIconDisplay: false}})
            return
        }

        const {startYearMonth, endYearMonth} = this.props.accountingTerm

        // アイコン非表示となるタイムスタンプ。会計期間の開始～終了のタイムスタンプだと判定がシビアすぎるので、少し余裕を持たせる。
        const adjustedMinTime = moment(startYearMonth, 'YYYYMM').startOf('month').add('1', 'day').valueOf()
        const adjustedMaxTime = moment(endYearMonth, 'YYYYMM').endOf('month').add('-1', 'day').valueOf()
        let nextIconDisplay: boolean
        let backIconDisplay: boolean

        if (visibleTimeStart < adjustedMinTime && visibleTimeEnd > adjustedMaxTime) {
            nextIconDisplay = false
            backIconDisplay = false
        } else if (visibleTimeStart < adjustedMinTime) {
            nextIconDisplay = true
            backIconDisplay = false
        } else if (visibleTimeEnd > adjustedMaxTime) {
            nextIconDisplay = false
            backIconDisplay = true
        } else {
            nextIconDisplay = true
            backIconDisplay = true
        }

        this.setState({navigateIcon: {nextIconDisplay: nextIconDisplay, backIconDisplay: backIconDisplay}})
    }

    timeLineScroll(invert: boolean): void {
        const month = this.calculateMonthDuration()

        if (!invert) {
            const visibleTimeEnd = this.state.visibleTimeEnd + month > this.state.maxTime ?
                this.state.maxTime : this.state.visibleTimeEnd + month
            const visibleTimeStart = visibleTimeEnd - month

            this.setState({
                visibleTimeStart: visibleTimeStart,
                visibleTimeEnd: visibleTimeEnd,
            })
            this.updateIconDisplay(visibleTimeStart, visibleTimeEnd)
        } else {
            const visibleTimeStart = this.state.visibleTimeStart - month < this.state.minTime ?
                this.state.minTime : this.state.visibleTimeStart - month
            const visibleTimeEnd = visibleTimeStart + month

            this.setState({
                visibleTimeStart: visibleTimeStart,
                visibleTimeEnd: visibleTimeEnd,
            })
            this.updateIconDisplay(visibleTimeStart, visibleTimeEnd)
        }
    }

    renderTimeLine(scheduleItem: Nullable<ScheduleItem>): Nullable<ReactElement> {
        if (!scheduleItem) {
            return null
        }
        const {width, navigateIcon, visibleTimeStart, visibleTimeEnd, monthsDiff} = this.state
        if (!width) {
            return null
        }

        return (
            <div style={{position: 'relative'}}>
                <TimeLineStyle monthsDiff={monthsDiff}>
                    <Timeline
                        stackItems
                        selected={[]}
                        lineHeight={64}
                        sidebarWidth={165}
                        defaultTimeStart={moment(visibleTimeStart, 'YYYYMM').valueOf()}
                        defaultTimeEnd={moment(visibleTimeEnd, 'YYYYMM').valueOf()}
                        visibleTimeStart={visibleTimeStart}
                        visibleTimeEnd={visibleTimeEnd}
                        minZoom={this.calculateMonthDuration()}
                        maxZoom={this.calculateMonthDuration()}
                        itemHeightRatio={1}
                        onTimeChange={this.handleTimeChange}
                        itemRenderer={(itemProps: ItemRendererProps<CustomItem>): ReactNode => {
                            const {item, itemContext, getItemProps, getResizeProps} = itemProps
                            const {left: leftResizeProps, right: rightResizeProps} = getResizeProps()
                            let color: string
                            if (item.categoryFlag) {
                                color = 'linear-gradient(0deg, rgba(0, 112, 176, 0.1), rgba(0, 112, 176, 0.1)),linear-gradient(0deg, #FFFFFF, #FFFFFF)'
                            } else if (item.stop) {
                                color = Colors.AccentLight
                            } else if (item.current) {
                                color = '#F5A6231A'
                            } else {
                                color = 'none'
                            }
                            return (
                                <div
                                    {...getItemProps({
                                        style: {
                                            position: 'absolute',
                                            cursor: 'auto',
                                            background: (item.categoryFlag) ?
                                                color : 'none',
                                            color: Colors.Body,
                                            border: 'none',
                                            zIndex: 81,
                                            height: item.height,
                                        },
                                    })}
                                >
                                    {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : null}
                                    <Box bgcolor={color}
                                         height={item.height}
                                         margin={0}
                                    >
                                        <ItemContainer>
                                            <Box ml={'15px'} mr={'15px'} lineHeight={'20px'}>
                                                {item.resultFiles &&
                                                item.resultFiles.length > 0 ?
                                                    <ResultLink
                                                        onMouseDown={() => this.props.onResultClick(
                                                            item.businessName ?? '',
                                                            item.resultFiles ?? [],
                                                        )}
                                                        onTouchEnd={(event: React.TouchEvent) => {
                                                            event.preventDefault()
                                                            this.props.onResultClick(
                                                                item.businessName ?? '',
                                                                item.resultFiles ?? [],
                                                            )
                                                        }}
                                                    >
                                                        {itemContext.title}
                                                    </ResultLink> :
                                                    <>
                                                        {itemContext.title}
                                                    </>
                                                }
                                            </Box>
                                        </ItemContainer>
                                    </Box>
                                    {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : null}
                                </div>
                            )
                        }}
                        groupRenderer={(
                            groupProps: ReactCalendarGroupRendererProps<CustomGroup>
                        ): ReactNode => {
                            const {
                                title,
                                categoryFlag,
                                className,
                                businessCategory,
                                businessInfo
                            } = groupProps.group

                            let rendererGroup: ReactNode = <></>

                            if (categoryFlag) {
                                rendererGroup =
                                    <CategoryName className={className}> {groupProps.group.title}</CategoryName>
                            } else if (businessInfo?.implementationTimes) {
                                rendererGroup =
                                    <Category display={'flex'} height={'100%'} className={className}
                                              style={{
                                                  justifyContent: 'center',
                                                  flexDirection: 'column',
                                                  'lineHeight': '18px'
                                              }}>
                                        <StretchColumnFlexContainer>
                                            <BusinessName>
                                                <LeftAlignLink
                                                    onClick={() =>
                                                        this.props.onBusinessNameClick(businessInfo)}
                                                    title={`${groupProps.group.title}`}
                                                >
                                                    {groupProps.group.title}
                                                </LeftAlignLink>
                                            </BusinessName>
                                            <Box height={'10px'}/>
                                            <Box lineHeight={'21px'}>
                                                実施回数　{businessInfo.implementationTimes}
                                            </Box>
                                        </StretchColumnFlexContainer>
                                    </Category>
                            } else if (businessInfo) {
                                rendererGroup =
                                    <Category display={'flex'} height={'100%'} className={className}>
                                        <BusinessName>
                                            <LeftAlignLink
                                                onClick={() => this.props.onBusinessNameClick(businessInfo)}
                                                title={`${groupProps.group.title}`}
                                            >
                                                {groupProps.group.title}
                                            </LeftAlignLink>
                                        </BusinessName>
                                    </Category>
                            }
                            return rendererGroup
                        }}
                        groups={scheduleItem.group}
                        items={scheduleItem.item}
                        canMove={false}
                        canResize={false}
                    >
                        <TimelineHeaders className={'sticky'}>
                            <>
                                {navigateIcon.backIconDisplay &&
                                    <Box position={'absolute'}
                                         top={'10px'}
                                         left={'155px'}
                                         zIndex={90}
                                    >
                                        <ScrollButton
                                            onClick={() => this.timeLineScroll(true)}
                                        >
                                            <BackIcon/>
                                        </ScrollButton>
                                    </Box>
                                }
                                {navigateIcon.nextIconDisplay &&
                                    <Box position={'absolute'}
                                         top={'10px'}
                                         right={'0px'}
                                         zIndex={90}
                                    >
                                        <ScrollButton
                                            onClick={() => this.timeLineScroll(false)}
                                        >
                                            <NextIcon/>
                                        </ScrollButton>
                                    </Box>
                                }
                            </>
                            {
                                <DateHeader
                                    {...{
                                        unit: 'year', height: 28,
                                        labelFormat: (date: any) => {
                                            const d = Array.isArray(date) ? date[0] : date;
                                            return moment(d.$d).tz('Asia/Tokyo').format('YYYY')
                                        }
                                    }}
                                    intervalRenderer={
                                        (intervalRenderProps?: IntervalRenderer<any>): ReactNode => {
                                            if (!intervalRenderProps) {
                                                return null
                                            }
                                            const {onClick, ...removeClickHandlerToDisableClickResizeProps} =
                                                intervalRenderProps.getIntervalProps()
                                            return <div
                                                {...removeClickHandlerToDisableClickResizeProps}
                                                onClick={() => {
                                                    return
                                                }}
                                                className={'rct-dateHeaderDay'}
                                            >
                                                <HeaderDay>
                                                    {intervalRenderProps.intervalContext.intervalText}
                                                </HeaderDay>
                                            </div>
                                        }}/>
                            }
                            <DateHeader
                                {...{
                                    unit: 'month', height: 28,
                                    labelFormat: (date: any) => {
                                        const d = Array.isArray(date) ? date[0] : date;
                                        return moment(d.$d).tz('Asia/Tokyo').format('YYYYMM')
                                    }
                                }}

                                intervalRenderer={
                                    (intervalRenderProps?: IntervalRenderer<any>): ReactNode => {
                                        if (!intervalRenderProps) {
                                            return null
                                        }
                                        const {intervalContext} = intervalRenderProps

                                        const current = this.state.currentTime.format('YYYYMM')
                                        const displayMonth =
                                            moment(intervalContext.intervalText, 'YYYYMM').month() + 1

                                        const {onClick, ...removeClickHandlerToDisableClickResizeProps} =
                                            intervalRenderProps.getIntervalProps()
                                        // @ts-ignore
                                        return <HeaderMonth
                                                current={current === intervalContext.intervalText}
                                                {...removeClickHandlerToDisableClickResizeProps}
                                                className={'rct-dateHeader'}>
                                                {displayMonth}
                                                <Box width={'50px'} />
                                                <DownloadIconLink
                                                    onClick={() =>
                                                        this.props.onDownloadIconClick(intervalContext.intervalText)
                                                    }
                                                    disabled={
                                                        !this.props.mergedFileSizeByYearMonthMap
                                                            .has(intervalContext.intervalText)
                                                    }
                                                >
                                                    <DownloadIcon icon={faDownload}/>
                                                </DownloadIconLink>
                                        </HeaderMonth>
                                    }
                                }
                            />
                        </TimelineHeaders>
                    </Timeline>
                </TimeLineStyle>
            </div>
        )
    }

    render(): Nullable<ReactElement> {
        return (
            <div>
                {this.renderTimeLine(this.state.schedule)}
            </div>
        )
    }
}

export default WithWidth(NatAssocWorkReportTimelineBase)

const ScrollButton = withStyles({
    root: {
        padding: 0,
    },
})(IconButton)

const BackIcon = withStyles({
    root: {
        color: '#70707080',
        fontSize: 40,
        zIndex: 1,
        cursor: 'pointer',
    },
})(NavigateBefore)

const NextIcon = withStyles({
    root: {
        color: '#70707080',
        fontSize: 40,
        zIndex: 1,
        cursor: 'pointer',
    },
})(NavigateNext)

const BusinessName = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const LeftAlignLink = styled(PlainLinkStyledButton)`
  font-size: ${FontSize.Middle};
  text-align: left;
  height: 100%;
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const ResultLink = styled(PlainLinkStyledButton)`
  font-size: ${FontSize.Middle};
  text-align: left;
  height: 100%;
  width: 100%;
  line-height: 20px;
  text-decoration: underline;
`

const DownloadIconLink = styled(PlainLinkStyledButton)`
    padding-right: 12px;
  :disabled {
    color: ${Colors.Body};
    pointer-events: none;
  }
`

const DownloadIcon = styled(FontAwesomeIcon)`
  font-size: ${FontSize.Small};
`

const ItemContainer = styled(ColumnFlexContainer)`
  justify-content: center;
  align-items: start;
  height: 100%;
  font-size: ${FontSize.Middle};
  overflow-wrap: anywhere;
`

const CategoryName = styled.div`
  height: 64px;
  width: 165px;
  padding-left: 5px;
  background: linear-gradient(0deg, rgba(0, 112, 176, 0.1), rgba(0, 112, 176, 0.1)),linear-gradient(0deg, #FFFFFF, #FFFFFF);
  font-size: ${FontSize.Small};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const Category = styled(Box)`
  padding: 0 5px 0 15px;
  border-right: 1px solid #d9d9d9;
  height: 100%;
`

const HeaderMonth = styled.div<{ current: boolean }>`
    display: flex;
  ${props => props.current ? `
    background: linear-gradient(0deg, rgba(245, 166, 35, 0.5), rgba(245, 166, 35, 0.5)),linear-gradient(0deg, #FFFFFF, #FFFFFF)!important;
    font-weight: ${FontWeights.Bold}
  ` : ''}
`

const HeaderDay = styled.div`
  position: sticky;
  left: 10px;
  top: 0;
  display: inline-block;
  font-size: ${FontSize.Small};
`

const TimeLineStyle = styled.div<{ monthsDiff: number }>`
  .react-calendar-timeline {
    font-size: ${FontSize.Small};
    border-top: 1px solid ${Colors.Surface};
    border-bottom: 1px solid ${Colors.Surface};
  }

  .react-calendar-timeline .rct-horizontal-lines .rct-hl-odd {
    background: rgba(0, 0, 0, 0);
  }

  .react-calendar-timeline .rct-dateHeader {
    display: flex;
    align-items: center;
    justify-content: space-between !important;
    height: 100%;
    border-bottom: none;
    cursor: default;
    background: linear-gradient(0deg, rgba(0, 112, 176, 0.1), rgba(0, 112, 176, 0.1)), linear-gradient(0deg, #FFFFFF, #FFFFFF);
    border-left: 1px solid ${Colors.Surface};
    font-size: ${FontSize.Small};
    padding: 0 15px;
  }

  .react-calendar-timeline .rct-dateHeaderDay {
    display: flex;
    align-items: center;
    height: 100%;
    border-bottom: 1px solid ${Colors.Surface};
    cursor: default;
    background: linear-gradient(0deg, rgba(0, 112, 176, 0.1), rgba(0, 112, 176, 0.1)), linear-gradient(0deg, #FFFFFF, #FFFFFF);
    border-left: 1px solid ${Colors.Surface};
    justify-content: flex-start;
    padding: 0 10px 0 15px;
  }

  .react-calendar-timeline .rct-calendar-header {
    border-top: 1px solid #d9d9d9;
    border-bottom: none;
  }

  .react-calendar-timeline .rct-vertical-lines .rct-vl.rct-vl-first {
    border-left: 1px dashed #d9d9d9;
  }

  .react-calendar-timeline .rct-header-root {
    background: #FFFFFF;
    border-bottom: none;
  }

  .react-calendar-timeline .rct-header-root > div:first-child {
    background-color: rgba(0, 0, 0, 0);
    border-right: 1px solid #d9d9d9;
  }

  .react-calendar-timeline .rct-header-root > div:first-child > div {
    border: none;
  }

  .react-calendar-timeline .rct-sidebar {
    background: ${Colors.Surface};
    border-right: none;
  }

  .react-calendar-timeline .rct-sidebar .rct-sidebar-row {
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    box-sizing: border-box;
    margin: 0;
    border-top: 1px solid #d9d9d9;
    border-bottom: none;
    background: transparent;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .react-calendar-timeline .rct-sidebar .rct-sidebar-row:has(.custom-group) {
    border-top: none;
  }

  .react-calendar-timeline .rct-sidebar .rct-sidebar-row.rct-sidebar-row-odd {
    background: transparent;
  }

  .rct-outer {
    border-left: none;
    border-right: none;
  }

  .timeline-out-of-target {
    background: #b0b0b0 !important;
  }

  .react-calendar-timeline .rct-scroll {
    border-top: none;
    background: ${Colors.Surface};
  }

  .react-calendar-timeline .rct-vertical-lines .rct-vl {
    position: absolute;
    z-index: 81 !important;
    border-left: 1px dashed #d9d9d9;
    ${props => props.monthsDiff === 0 ? `
    display: none; :
  ` : ''}
  }

  .react-calendar-timeline .rct-horizontal-lines .rct-hl-even,
  .react-calendar-timeline .rct-horizontal-lines .rct-hl-odd {
    border-top: 1px dashed #d9d9d9;
    border-bottom: none;
    box-sizing: border-box;
  }

  .react-calendar-timeline .rct-horizontal-lines .rct-hl-even:first-child,
  .react-calendar-timeline .rct-horizontal-lines .rct-hl-odd:first-child {
    border-top: none;
  }

  .react-calendar-timeline .rct-calendar-header {
    border: none;
  }

  .react-calendar-timeline .rct-vertical-lines .rct-vl.rct-day-6,
  .react-calendar-timeline .rct-vertical-lines .rct-vl.rct-day-0 {
    background: none;
  }
`
